import React, {useEffect, useRef, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Container,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Select,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import {getUser} from '../helpers/crypto';
import {Category} from '../helpers/categories';
import {Constants} from '../components/Constants';

interface ImgSlot {
    preview: string | null;
    file: File | null;
    serverName: string;
    clear: boolean;
}

const emptySlot = (): ImgSlot => ({ preview: null, file: null, serverName: '', clear: false });

const ItemPage: React.FC = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const editId = params.get('id') ? parseInt(params.get('id')!) : 0;
    const isEdit = editId > 0;

    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [categories, setCategories] = useState<Category[]>([]);

    useEffect(() => {
        fetch(`${Constants.SERVER_URL}${Constants.GET_CATEGORIES_PREFIX}`)
            .then(r => r.json())
            .then(d => { if (d.success) setCategories(d.categories); })
            .catch(() => {});
    }, []);

    // Form fields
    const [category, setCategory]     = useState(1);
    const [type, setType]             = useState(0);
    const [shortName, setShortName]   = useState('');
    const [name, setName]             = useState('');
    const [description, setDescription] = useState('');
    const [kol, setKol]               = useState(0);
    const [active, setActive]         = useState(true);
    const [onStock, setOnStock]       = useState(false);
    const [price, setPrice]           = useState('');
    const [priceOpt, setPriceOpt]     = useState('');
    const [priceAkcia, setPriceAkcia] = useState('');

    // Images: 3 slots
    const [imgs, setImgs] = useState<[ImgSlot, ImgSlot, ImgSlot]>([emptySlot(), emptySlot(), emptySlot()]);
    const fileRefs = [useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)];

    useEffect(() => {
        const user = getUser();
        if (!user) { navigate('/login'); return; }
        if (user.status !== 'ADMIN') { navigate('/catalog'); return; }

        if (!isEdit) return;

        fetch(`${Constants.SERVER_URL}${Constants.GET_ITEM_PREFIX}?id=${editId}`)
            .then(r => r.json())
            .then(data => {
                if (!data.success) { setError('Товар не знайдено'); setLoading(false); return; }
                const item = data.item;
                setCategory(item.category);
                setType(item.type);
                setShortName(item.shortName);
                setName(item.name);
                setDescription(item.description || '');
                setKol(item.kol);
                setActive(item.active);
                setOnStock(item.onStock);
                setPrice(String(item.price));
                setPriceOpt(String(item.priceOpt));
                setPriceAkcia(String(item.priceAkcia));

                const extractFilename = (url: string | null): string => {
                    if (!url) return '';
                    return url.split('/').pop() || '';
                };

                setImgs([
                    { preview: item.imgPath1, file: null, serverName: extractFilename(item.imgPath1), clear: false },
                    { preview: item.imgPath2, file: null, serverName: extractFilename(item.imgPath2), clear: false },
                    { preview: item.imgPath3, file: null, serverName: extractFilename(item.imgPath3), clear: false },
                ]);
                setLoading(false);
            })
            .catch(() => { setError('Помилка завантаження товару'); setLoading(false); });
    }, [editId, isEdit, navigate]);

    const handleFileChange = (idx: number, file: File | null) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            setImgs(prev => {
                const next: [ImgSlot, ImgSlot, ImgSlot] = [...prev] as any;
                next[idx] = { preview: e.target?.result as string, file, serverName: prev[idx].serverName, clear: false };
                return next;
            });
        };
        reader.readAsDataURL(file);
    };

    const clearSlot = (idx: number) => {
        setImgs(prev => {
            const next: [ImgSlot, ImgSlot, ImgSlot] = [...prev] as any;
            next[idx] = { preview: null, file: null, serverName: '', clear: true };
            return next;
        });
        if (fileRefs[idx].current) fileRefs[idx].current!.value = '';
    };

    const handleSubmit = async () => {
        if (!name.trim()) { setError("Назва товару обов'язкова"); return; }
        if (!price || isNaN(Number(price))) { setError('Введіть коректну ціну'); return; }

        setSaving(true);
        setError('');
        setSuccess('');

        const fd = new FormData();
        if (isEdit) fd.append('id', String(editId));
        fd.append('category', String(category));
        fd.append('type', String(type));
        fd.append('shortName', shortName);
        fd.append('name', name);
        fd.append('description', description);
        fd.append('kol', String(kol));
        fd.append('active', active ? '1' : '0');
        fd.append('onStock', onStock ? '1' : '0');
        fd.append('price', price);
        fd.append('priceOpt', priceOpt || '0');
        fd.append('priceAkcia', priceAkcia || '0');

        // Pass existing server filenames so PHP keeps them if no new file
        fd.append('imgPath1', imgs[0].serverName);
        fd.append('imgPath2', imgs[1].serverName);
        fd.append('imgPath3', imgs[2].serverName);

        if (imgs[0].clear) fd.append('clearImg1', '1');
        if (imgs[1].clear) fd.append('clearImg2', '1');
        if (imgs[2].clear) fd.append('clearImg3', '1');

        if (imgs[0].file) fd.append('img1', imgs[0].file);
        if (imgs[1].file) fd.append('img2', imgs[1].file);
        if (imgs[2].file) fd.append('img3', imgs[2].file);

        try {
            const res = await fetch(`${Constants.SERVER_URL}${Constants.SAVE_ITEM_PREFIX}`, {
                method: 'POST',
                body: fd,
            });
            const data = await res.json();
            if (data.success) {
                setSuccess(isEdit ? 'Товар збережено!' : `Товар додано (ID: ${data.id})`);
                if (!isEdit) {
                    setTimeout(() => navigate(`/item?id=${data.id}`), 1200);
                }
            } else {
                setError(data.error || 'Помилка збереження');
            }
        } catch {
            setError('Не вдалося з\'єднатися з сервером');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ bgcolor: '#f7f9fc', minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', borderBottom: '1px solid #e0e6ed', position: 'sticky', top: 0, zIndex: 100 }}>
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', alignItems: 'center', height: 70, gap: 2 }}>
                        <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', gap: 1, textDecoration: 'none' }}>
                            <LocalHospitalIcon sx={{ color: '#0077CC', fontSize: 28 }} />
                            <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#1a1a2e' }}>{Constants.APP_LABEL}</Typography>
                        </Box>
                        <Box sx={{ flexGrow: 1 }} />
                        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/catalog')}
                            sx={{ textTransform: 'none', color: '#555' }}>
                            Каталог
                        </Button>
                    </Box>
                </Container>
            </Box>

            <Container maxWidth="md" sx={{ py: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                    {isEdit ? `Редагування товару #${editId}` : 'Новий товар'}
                </Typography>

                {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                <Paper elevation={0} sx={{ border: '1px solid #e0e6ed', borderRadius: 3, p: 3 }}>

                    {/* ── Images ─────────────────────────────────── */}
                    <Typography sx={{ fontWeight: 600, mb: 2 }}>Фотографії (до 3 шт.)</Typography>
                    <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2 }, flexWrap: 'wrap', mb: 3 }}>
                        {imgs.map((slot, idx) => (
                            <Box key={idx} sx={{ position: 'relative', width: { xs: 100, sm: 160 }, height: { xs: 100, sm: 160 } }}>
                                <input
                                    ref={fileRefs[idx]}
                                    type="file"
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    onChange={e => handleFileChange(idx, e.target.files?.[0] ?? null)}
                                />
                                {slot.preview ? (
                                    <Box sx={{ width: '100%', height: '100%', borderRadius: 2, border: '1px solid #e0e6ed', overflow: 'hidden', position: 'relative' }}>
                                        <Box
                                            component="img"
                                            src={slot.preview}
                                            alt={`img${idx + 1}`}
                                            sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 1 }}
                                        />
                                        <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.5 }}>
                                            <Tooltip title="Замінити фото">
                                                <IconButton size="small"
                                                    onClick={() => fileRefs[idx].current?.click()}
                                                    sx={{ bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: '#fff' } }}>
                                                    <AddPhotoAlternateOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Видалити фото">
                                                <IconButton size="small"
                                                    onClick={() => clearSlot(idx)}
                                                    sx={{ bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { color: '#e53935', bgcolor: '#fff' } }}>
                                                    <DeleteOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box
                                        onClick={() => fileRefs[idx].current?.click()}
                                        sx={{
                                            width: '100%', height: '100%', borderRadius: 2,
                                            border: '2px dashed #ccd6e0', display: 'flex',
                                            flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                            cursor: 'pointer', color: '#aaa',
                                            '&:hover': { borderColor: '#0077CC', color: '#0077CC', bgcolor: '#f0f7ff' },
                                            transition: 'all 0.2s',
                                        }}>
                                        <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 36, mb: 0.5 }} />
                                        <Typography sx={{ fontSize: 12 }}>Фото {idx + 1}</Typography>
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {/* ── Main fields ─────────────────────────────── */}
                    <Typography sx={{ fontWeight: 600, mb: 2 }}>Основна інформація</Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography sx={{ fontSize: 13, color: '#555', mb: 0.5 }}>Категорія</Typography>
                            <Select fullWidth size="small" value={category} onChange={e => setCategory(Number(e.target.value))}>
                                {categories.map(c => (
                                    <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>
                                ))}
                            </Select>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography sx={{ fontSize: 13, color: '#555', mb: 0.5 }}>Тип (числовий код)</Typography>
                            <TextField fullWidth size="small" type="number" value={type}
                                onChange={e => setType(parseInt(e.target.value) || 0)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography sx={{ fontSize: 13, color: '#555', mb: 0.5 }}>Коротка назва (артикул)</Typography>
                            <TextField fullWidth size="small" value={shortName}
                                onChange={e => setShortName(e.target.value)} placeholder="напр. GAMMA-32" />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 8 }}>
                            <Typography sx={{ fontSize: 13, color: '#555', mb: 0.5 }}>Повна назва *</Typography>
                            <TextField fullWidth size="small" value={name}
                                onChange={e => setName(e.target.value)} placeholder="Назва товару" />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Typography sx={{ fontSize: 13, color: '#555', mb: 0.5 }}>Опис</Typography>
                            <TextField fullWidth size="small" multiline rows={3} value={description}
                                onChange={e => setDescription(e.target.value)} placeholder="Детальний опис товару..." />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography sx={{ fontSize: 13, color: '#555', mb: 0.5 }}>Кількість</Typography>
                            <TextField fullWidth size="small" type="number" value={kol}
                                onChange={e => setKol(parseInt(e.target.value) || 0)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', pt: 1 }}>
                                <FormControlLabel
                                    control={<Checkbox checked={active} onChange={e => setActive(e.target.checked)} />}
                                    label="Активний" />
                                <FormControlLabel
                                    control={<Checkbox checked={onStock} onChange={e => setOnStock(e.target.checked)} />}
                                    label="В наявності" />
                            </Box>
                        </Grid>
                    </Grid>

                    <Divider sx={{ mb: 3 }} />

                    {/* ── Prices ──────────────────────────────────── */}
                    <Typography sx={{ fontWeight: 600, mb: 2 }}>Ціни (грн)</Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography sx={{ fontSize: 13, color: '#555', mb: 0.5 }}>Роздрібна (REGULAR) *</Typography>
                            <TextField fullWidth size="small" type="number" value={price}
                                onChange={e => setPrice(e.target.value)}
                                slotProps={{ input: { inputProps: { step: '0.01', min: '0' } } }} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography sx={{ fontSize: 13, color: '#555', mb: 0.5 }}>Оптова (OPT)</Typography>
                            <TextField fullWidth size="small" type="number" value={priceOpt}
                                onChange={e => setPriceOpt(e.target.value)}
                                slotProps={{ input: { inputProps: { step: '0.01', min: '0' } } }} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Typography sx={{ fontSize: 13, color: '#555', mb: 0.5 }}>Акційна (SUPEROPT)</Typography>
                            <TextField fullWidth size="small" type="number" value={priceAkcia}
                                onChange={e => setPriceAkcia(e.target.value)}
                                slotProps={{ input: { inputProps: { step: '0.01', min: '0' } } }} />
                        </Grid>
                    </Grid>

                    {/* ── Actions ─────────────────────────────────── */}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'flex-end' }}>
                        <Button variant="outlined" onClick={() => navigate('/catalog')}
                            sx={{ textTransform: 'none', borderRadius: 2 }}>
                            Скасувати
                        </Button>
                        <Button variant="contained" onClick={handleSubmit} disabled={saving}
                            sx={{ textTransform: 'none', borderRadius: 2, px: 4 }}>
                            {saving ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Зберегти' : 'Додати товар')}
                        </Button>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default ItemPage;
